import { Component } from '@theme/component';
import { isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';
import { REVEAL_STYLES, resolveRevealStyle, revealDelay, revealVars, splitWords } from '@theme/eme-reveal-core';

const HOST_ITEMS = '.section-resource-list__header, .resource-list__item';

class EmeReveal extends Component {
  #internals = null;
  #observer = null;
  #settleTimer = null;
  #done = false;
  #host = null;
  #activeStyle = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.#done || prefersReducedMotion() || window.Shopify?.designMode) return;
    const style = resolveRevealStyle(this.dataset.style ?? 'fade-up', this.dataset.mobileStyle ?? '', isMobileBreakpoint());
    if (!style) return;
    this.#activeStyle = style;
    this.dataset.activeStyle = style;
    if (this.dataset.target === 'section-list') {
      this.#observeHost();
      return;
    }
    this.#internals ??= this.attachInternals?.() ?? null;
    const states = this.#internals?.states;
    if (!states) return;
    this.#splitWords();
    this.#applyVars(this);
    states.add('pending');
    this.#observer = new IntersectionObserver(this.#handleIntersect, { threshold: this.#threshold });
    for (const target of this.#targets()) this.#observer.observe(target);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    clearTimeout(this.#settleTimer);
    if (this.#host) delete this.#host.dataset.emeReveal;
    if (!this.#done) this.#internals?.states.delete('pending');
    this.#internals?.states.delete('revealing');
  }

  get #threshold() {
    return REVEAL_STYLES[this.#style]?.threshold ?? Number(this.dataset.threshold ?? 0.15);
  }

  get #style() {
    return this.#activeStyle ?? this.dataset.style ?? 'fade-up';
  }

  #applyVars(element, style = this.#style) {
    for (const [name, value] of Object.entries(revealVars(style))) element.style.setProperty(name, value);
  }

  #observeHost() {
    const host = this.closest('.shopify-section');
    if (!host || !host.querySelector(HOST_ITEMS)) return;
    this.#host = host;
    this.#applyVars(host);
    const { headerStyle } = REVEAL_STYLES[this.#style] ?? {};
    const header = host.querySelector('.section-resource-list__header');
    if (headerStyle && header) this.#applyVars(header, headerStyle);
    host.dataset.emeReveal = 'pending';
    this.#observer = new IntersectionObserver(this.#handleIntersect, { threshold: this.#threshold });
    this.#observer.observe(host);
  }

  #splitWords() {
    if (this.#style !== 'words' || this.dataset.split === 'true') return;
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      const parent = node.parentElement;
      if (!node.textContent?.trim() || parent?.closest('eme-reveal') !== this || parent.closest('script, style')) continue;
      const fragment = document.createDocumentFragment();
      for (const token of splitWords(node.textContent)) {
        if (!token.trim()) {
          fragment.append(token);
          continue;
        }
        const word = document.createElement('span');
        word.className = 'eme-reveal__item eme-reveal__word';
        word.textContent = token;
        fragment.append(word);
      }
      node.replaceWith(fragment);
    }
    this.dataset.split = 'true';
  }

  #staggered() {
    const items = [...this.querySelectorAll('.eme-reveal__item, .eme-reveal__items > *')].filter(
      (item) => item.closest('eme-reveal') === this
    );
    return items.length ? items : [...this.children];
  }

  #targets() {
    const section = this.dataset.trigger === 'section' ? this.closest('.shopify-section') : null;
    if (section) return [section];
    return this.children.length ? [...this.children] : [this];
  }

  #settle(count, onDone) {
    const { duration } = REVEAL_STYLES[this.#style] ?? REVEAL_STYLES['fade-up'];
    const settleMs = duration + revealDelay(Math.max(count - 1, 0), this.#style);
    this.#settleTimer = setTimeout(onDone, settleMs + 50);
  }

  #revealHost() {
    const host = this.#host;
    const items = [...host.querySelectorAll('.resource-list__item')];
    for (const [index, item] of items.entries()) item.style.setProperty('--eme-reveal-delay', `${revealDelay(index, this.#style)}ms`);
    this.#done = true;
    host.dataset.emeReveal = 'revealing';
    this.#settle(items.length, () => delete host.dataset.emeReveal);
  }

  #reveal() {
    if (this.#host) {
      this.#revealHost();
      return;
    }
    const staggered = this.#staggered();
    for (const [index, element] of staggered.entries()) {
      element.style.setProperty('--eme-reveal-delay', `${revealDelay(index, this.#style)}ms`);
    }
    this.#done = true;
    const states = this.#internals?.states;
    states?.add('revealing');
    states?.delete('pending');
    this.#settle(staggered.length, () => states?.delete('revealing'));
  }

  #handleIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#reveal();
  };
}

if (!customElements.get('eme-reveal')) customElements.define('eme-reveal', EmeReveal);
